import React, { useState, useEffect, useCallback } from "react";
import {
  Users, Plus, MessageSquare, Award, Loader2, Search,
  ArrowLeft, StickyNote, Clock, Send, User, Phone, PhoneCall, Mail, MapPin, Tag
} from "lucide-react";
import ChatbotTester from "./ChatbotTester";
import SendBookingLinkCard from "./SendBookingLinkCard";
import BookingsTable from "./BookingsTable";
import { MagicPreviewsPanel } from "./crm/MagicPreviewsPanel";
import { logger } from "../utils/logger";
import { formatDateTime, formatEventDateTime } from "../utils/datetime";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CRMTab = ({ auth, authAxios, searchQuery = "", bookings = [] }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const [filterStatus, setFilterStatus] = useState("all");
  const [customerDetail, setCustomerDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [newTag, setNewTag] = useState("");
  const [waMessage, setWaMessage] = useState("");
  const [sendingWa, setSendingWa] = useState(false);
  const [aiCalling, setAiCalling] = useState(false);

  const triggerAICall = async () => {
    if (!customerDetail?.phone) return;
    if (!window.confirm(`AI agent will call ${customerDetail.phone} now and walk them through 6 booking questions. Continue?`)) return;
    setAiCalling(true);
    try {
      const res = await authAxios.post(`${API_URL}/api/admin/doozyphone/call/initiate`, {
        phone: customerDetail.phone, name: customerDetail.name || "",
        lead_id: customerDetail.phone,
      });
      window.alert(`Calling now (SID: ${res.data.call_sid}). Transcript will appear in the call log.`);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't start the AI call. Check Twilio config in .env.");
    }
    setAiCalling(false);
  };

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API_URL}/api/admin/customers`);
      setCustomers(res.data);
    } catch (err) { logger.error(err); }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);
  useEffect(() => { if (searchQuery) setSearchTerm(searchQuery); }, [searchQuery]);

  const fetchCustomerDetail = async (phone) => {
    setDetailLoading(true);
    try {
      const res = await authAxios.get(`${API_URL}/api/admin/customers/${phone}`);
      setCustomerDetail(res.data);
    } catch (err) { logger.error(err); }
    setDetailLoading(false);
  };

  const updateLeadStatus = async (phone, status) => {
    await authAxios.put(`${API_URL}/api/admin/customers/${phone}/status`, { lead_status: status });
    await fetchCustomers();
    if (customerDetail?.phone === phone) {
      setCustomerDetail({ ...customerDetail, lead_status: status });
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !customerDetail) return;
    await authAxios.post(`${API_URL}/api/admin/customers/${customerDetail.phone}/notes`, { note: newNote });
    setNewNote("");
    await fetchCustomerDetail(customerDetail.phone);
  };

  const addTag = async () => {
    if (!newTag.trim() || !customerDetail) return;
    await authAxios.post(`${API_URL}/api/admin/customers/${customerDetail.phone}/tags`, { tag: newTag });
    setNewTag("");
    await fetchCustomerDetail(customerDetail.phone);
  };

  const sendWhatsApp = async () => {
    if (!waMessage.trim() || !customerDetail) return;
    setSendingWa(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/customers/${customerDetail.phone}/whatsapp`, { message: waMessage });
      setWaMessage("");
      await fetchCustomerDetail(customerDetail.phone);
    } catch (err) {
      alert("Failed to send WhatsApp message");
    }
    setSendingWa(false);
  };

  const filtered = customers.filter(c => {
    const matchesSearch = searchTerm === "" ||
      (c.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.phone || "").includes(searchTerm) ||
      (c.email || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || c.lead_status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const statusColors = {
    new: "bg-blue-100 text-blue-700",
    contacted: "bg-yellow-100 text-yellow-700",
    qualified: "bg-purple-100 text-purple-700",
    converted: "bg-green-100 text-green-700",
    lost: "bg-red-100 text-red-700"
  };

  return (
    <div className="space-y-6" data-testid="crm-tab">
      {/* Bookings Table */}
      <BookingsTable bookings={bookings} auth={auth} />

      {/* AI Chatbot Tester */}
      <ChatbotTester auth={auth} />

      {/* Ops: send a secure booking-completion link */}
      <SendBookingLinkCard authAxios={authAxios} />

      {/* Customer Detail View */}
      {customerDetail ? (
        <div className="bg-white rounded-3xl shadow-lg p-6" data-testid="customer-detail">
          <button onClick={() => setCustomerDetail(null)} className="flex items-center gap-2 text-[#475569] hover:text-[#E83E8C] mb-4">
            <ArrowLeft size={16} /> Back to list
          </button>

          {detailLoading ? (
            <div className="text-center py-8"><Loader2 className="animate-spin mx-auto text-[#E83E8C]" /></div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Left: Info + Bookings */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] rounded-full flex items-center justify-center text-white font-bold text-2xl">
                    {(customerDetail.name || "?")[0].toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#1E1B4B]">{customerDetail.name || "Unknown"}</h3>
                    <p className="text-sm text-[#475569] flex items-center gap-2 flex-wrap">
                      <Phone size={12} /> {customerDetail.phone}
                      <button
                        onClick={triggerAICall}
                        disabled={aiCalling}
                        className="ml-2 px-2.5 py-1 rounded-lg bg-gradient-to-r from-indigo-600 to-[#E83E8C] text-white text-[11px] font-bold flex items-center gap-1 hover:opacity-90 disabled:opacity-50"
                        data-testid="ai-call-lead-btn"
                        title="DoozyPhone — AI agent will call this lead and walk them through 6 booking questions"
                      >
                        {aiCalling ? <Loader2 size={11} className="animate-spin" /> : <PhoneCall size={11} />}
                        AI Call
                      </button>
                    </p>
                    {customerDetail.email && <p className="text-sm text-[#475569] flex items-center gap-2"><Mail size={12} /> {customerDetail.email}</p>}
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#475569]">Status:</span>
                  <select value={customerDetail.lead_status || "new"} onChange={e => updateLeadStatus(customerDetail.phone, e.target.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border-0 outline-none cursor-pointer ${statusColors[customerDetail.lead_status] || statusColors.new}`}
                    data-testid="detail-status-select">
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                    <option value="converted">Converted</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>

                {/* Tags */}
                <div>
                  <p className="text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-1"><Tag size={12} /> Tags</p>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {(customerDetail.tags || []).map((t, i) => (
                      <span key={`tag-${t}`} className="px-2 py-0.5 bg-[#E83E8C]/10 text-[#E83E8C] rounded-full text-xs font-medium">{t}</span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="Add tag"
                      className="flex-1 px-3 py-1.5 rounded-lg border text-sm focus:border-[#E83E8C] outline-none" data-testid="add-tag-input"
                      onKeyDown={e => e.key === 'Enter' && addTag()} />
                    <button onClick={addTag} className="px-3 py-1.5 bg-[#E83E8C] text-white rounded-lg text-sm" data-testid="add-tag-btn">Add</button>
                  </div>
                </div>

                {/* Bookings */}
                <div>
                  <h4 className="font-semibold text-[#1E1B4B] mb-2">Booking History</h4>
                  {(customerDetail.bookings || []).length === 0 ? (
                    <p className="text-sm text-[#94a3b8]">No bookings yet</p>
                  ) : customerDetail.bookings.map((b, i) => (
                    <div key={b.date ? `${b.date}-${b.package}` : `booking-${i}`} className="p-3 bg-[#FFFAF5] rounded-xl mb-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[#1E1B4B]">{formatEventDateTime(b.date, b.setup_time || b.event_time || b.time_slot)}</span>
                        <span className="px-2 py-0.5 bg-[#E83E8C]/10 text-[#E83E8C] rounded-full text-xs">{b.package}</span>
                      </div>
                      {b.created_at && <p className="text-[11px] text-[#94a3b8] mt-1 flex items-center gap-1"><Clock size={10} /> Created {formatDateTime(b.created_at)}</p>}
                      {b.address && <p className="text-xs text-[#475569] mt-1 flex items-center gap-1"><MapPin size={10} /> {b.address}</p>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Magic AI Previews */}
              <MagicPreviewsPanel
                previews={customerDetail.magic_previews || []}
                phone={customerDetail.phone}
                authAxios={authAxios}
              />

              {/* Right: Notes + WhatsApp */}
              <div className="space-y-4">
                {/* Notes */}
                <div>
                  <h4 className="font-semibold text-[#1E1B4B] mb-2 flex items-center gap-1"><StickyNote size={14} /> Notes</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto mb-2">
                    {(customerDetail.notes || []).length === 0 ? (
                      <p className="text-sm text-[#94a3b8]">No notes yet</p>
                    ) : customerDetail.notes.map((n, i) => (
                      <div key={n.created_at || `note-${i}`} className="p-2 bg-yellow-50 rounded-lg text-sm">
                        <p className="text-[#1E1B4B]">{n.text}</p>
                        <p className="text-xs text-[#94a3b8] mt-1 flex items-center gap-1"><Clock size={10} /> {formatDateTime(n.created_at)} by {n.author}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..."
                      className="flex-1 px-3 py-1.5 rounded-lg border text-sm focus:border-[#E83E8C] outline-none" data-testid="add-note-input"
                      onKeyDown={e => e.key === 'Enter' && addNote()} />
                    <button onClick={addNote} className="px-3 py-1.5 bg-[#FF9F1C] text-white rounded-lg text-sm" data-testid="add-note-btn">Add</button>
                  </div>
                </div>

                {/* WhatsApp */}
                <div>
                  <h4 className="font-semibold text-[#1E1B4B] mb-2 flex items-center gap-1"><MessageSquare size={14} className="text-[#25D366]" /> WhatsApp</h4>
                  <div className="bg-[#e5ddd5] rounded-xl p-3 max-h-64 overflow-y-auto space-y-2 mb-2">
                    {customerDetail.messages?.length === 0 ? (
                      <p className="text-sm text-[#94a3b8] text-center py-2">No messages yet</p>
                    ) : customerDetail.messages?.map((m, i) => (
                      <div key={m.timestamp || `msg-${i}`} className={`p-2 rounded-lg text-sm max-w-[85%] ${m.direction === 'outbound' || m.direction === 'outbound_ai' ? 'ml-auto bg-[#dcf8c6]' : 'bg-white border border-gray-200'}`}>
                        <p className="text-[#1E1B4B] whitespace-pre-wrap">{m.message}</p>
                        <p className="text-xs text-[#94a3b8] mt-1">
                          {m.direction === 'outbound' ? 'Sent' : m.direction === 'outbound_ai' ? 'AI Bot' : 'Received'} · {formatDateTime(m.timestamp)}
                          {m.status === 'failed' && <span className="text-red-500 ml-1">(Failed)</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={waMessage} onChange={e => setWaMessage(e.target.value)} placeholder="Type a message..."
                      className="flex-1 px-3 py-2 rounded-lg border text-sm focus:border-[#25D366] outline-none" data-testid="wa-message-input"
                      onKeyDown={e => e.key === 'Enter' && sendWhatsApp()} />
                    <button onClick={sendWhatsApp} disabled={sendingWa || !waMessage.trim()}
                      className="px-4 py-2 bg-[#25D366] text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-1"
                      data-testid="wa-send-btn">
                      <Send size={14} /> Send
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Customer List */
        <div className="bg-white rounded-3xl shadow-lg p-6" data-testid="crm-customer-list">
          <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-4 flex items-center gap-2">
            <Users size={20} className="text-[#E83E8C]" /> Customers ({filtered.length})
          </h2>

          {/* Search & Filter */}
          <div className="flex gap-3 mb-4">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
              <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by name, phone, or email..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm"
                data-testid="crm-search-input" />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" data-testid="crm-filter-status">
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
          </div>

          {loading ? (
            <div className="text-center py-8"><Loader2 className="animate-spin mx-auto text-[#E83E8C]" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-[#475569] py-8">No customers found. They'll appear when bookings, leads, or WhatsApp chats come in.</p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {filtered.map((c, i) => (
                <div key={c.phone || `customer-${i}`} onClick={() => fetchCustomerDetail(c.phone)}
                  className="flex items-center gap-4 p-3 rounded-xl border border-gray-100 hover:bg-[#FFFAF5] cursor-pointer transition-all"
                  data-testid={`customer-row-${i}`}>
                  <div className="w-10 h-10 bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {(c.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#1E1B4B] truncate">{c.name || "Unknown"}</p>
                    <p className="text-xs text-[#94a3b8]">{c.phone} {c.email && `| ${c.email}`}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[c.lead_status] || statusColors.new}`}>{c.lead_status || "new"}</span>
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-bold text-[#E83E8C]">{c.booking_count > 0 ? `${c.booking_count} bookings` : c.wa_msg_count > 0 ? `${c.wa_msg_count} msgs` : 'New'}</p>
                    <p className="text-xs text-[#94a3b8]">{c.source === 'whatsapp' ? 'WhatsApp' : c.source === 'lead' ? 'Lead' : c.source === 'booking' ? 'Booking' : c.source}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CRMTab;
